import { useQuery } from '@apollo/client';
import {
  TrendIntervalSize,
  useGetPhlebotomistTrendStatsQuery,
  useGetUsersListQuery,
  UserRole,
} from 'gql/types';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  getDateRangeBounds,
  usePhlebotomistStatsData,
} from './usePhlebotomistStatsData';

import { GET_APP_SETTINGS_QUERY } from '../../../settings/graphql/queries';
import {
  ComparisonStats,
  DashboardDisplayMode,
  DATE_RANGE_PREVIOUS_MAP,
  DateRangeType,
  DEFAULT_FILTERS,
  DonationGoalData,
  PaneFilters,
  PhlebotomistDashboardContextValue,
  PhlebotomistDashboardFilters,
  PhlebotomistStats,
  TrendStatsDataPoint,
  TrendsIntervalSize,
} from '../../types/phlebotomist-dashboard';

// Map frontend interval size to backend enum
const mapIntervalSizeToBackend = (
  intervalSize: TrendsIntervalSize
): TrendIntervalSize => {
  const mapping: Record<TrendsIntervalSize, TrendIntervalSize> = {
    day: TrendIntervalSize.Day,
    week: TrendIntervalSize.Week,
    month: TrendIntervalSize.Month,
    quarter: TrendIntervalSize.Quarter,
    year: TrendIntervalSize.Year,
  };
  return mapping[intervalSize];
};

const STORAGE_KEY = 'phlebotomist-dashboard-preferences';

// Load preferences from localStorage
function loadPreferences(): Partial<PhlebotomistDashboardFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        showRegionComparison: parsed.showRegionComparison ?? false,
        showCompanyComparison: parsed.showCompanyComparison ?? false,
      };
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

// Save preferences to localStorage
function savePreferences(prefs: {
  showRegionComparison: boolean;
  showCompanyComparison: boolean;
}) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore storage errors
  }
}

// ============ URL QUERY PARAMETER HELPERS ============
// Valid values for type-safe URL parameter parsing
const VALID_DATE_RANGES: ReadonlySet<string> = new Set<DateRangeType>([
  'day',
  'yesterday',
  'week',
  'lastWeek',
  'month',
  'lastMonth',
  'quarter',
  'lastQuarter',
  'year',
  'lastYear',
  'dayCal',
  'yesterdayCal',
  'weekCal',
  'lastWeekCal',
  'monthCal',
  'lastMonthCal',
  'quarterCal',
  'lastQuarterCal',
  'yearCal',
  'pYearCal',
  'custom',
]);
const VALID_DISPLAY_MODES: ReadonlySet<string> = new Set<DashboardDisplayMode>([
  'normal',
  'split',
  'inline',
  'trends',
]);
const VALID_TRENDS_INTERVALS: ReadonlySet<string> = new Set<TrendsIntervalSize>(
  ['day', 'week', 'month', 'quarter', 'year']
);

/** Read all dashboard filters from URL search params */
function filtersFromUrl(
  searchParams: URLSearchParams,
  storedPrefs: Partial<PhlebotomistDashboardFilters>
): PhlebotomistDashboardFilters {
  const base = { ...DEFAULT_FILTERS, ...storedPrefs };

  // Primary date range
  const dr = searchParams.get('dateRange');
  if (dr && VALID_DATE_RANGES.has(dr)) base.dateRange = dr as DateRangeType;

  // Custom date range
  const cs = searchParams.get('customStart');
  const ce = searchParams.get('customEnd');
  if (cs && ce && base.dateRange === 'custom') {
    const start = new Date(cs);
    const end = new Date(ce);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      base.customDateRange = { start, end };
    }
  }

  // Region IDs (comma-separated)
  const rIds = searchParams.get('regionIds');
  if (rIds !== null) {
    base.regionIds = rIds ? rIds.split(',') : [];
  }

  // Phlebotomist ID
  const pId = searchParams.get('phlebotomistId');
  if (pId !== null) {
    base.phlebotomistId = pId || undefined;
  }

  // Comparison toggles
  const sr = searchParams.get('showRegion');
  if (sr !== null) base.showRegionComparison = sr === '1';
  const sc = searchParams.get('showCompany');
  if (sc !== null) base.showCompanyComparison = sc === '1';

  // Display mode
  const dm = searchParams.get('displayMode');
  if (dm && VALID_DISPLAY_MODES.has(dm)) {
    base.displayMode = dm as DashboardDisplayMode;
  }

  // Trends interval
  const ti = searchParams.get('trendsInterval');
  if (ti && VALID_TRENDS_INTERVALS.has(ti)) {
    base.trendsIntervalSize = ti as TrendsIntervalSize;
  }

  // Initialize comparison pane filters for split/inline from URL or defaults
  if (base.displayMode === 'split' || base.displayMode === 'inline') {
    const cdr = searchParams.get('cDateRange');
    const compDateRange =
      cdr && VALID_DATE_RANGES.has(cdr)
        ? (cdr as DateRangeType)
        : DATE_RANGE_PREVIOUS_MAP[base.dateRange];

    let compCustomDateRange: { start: Date; end: Date } | undefined;
    const ccs = searchParams.get('cCustomStart');
    const cce = searchParams.get('cCustomEnd');
    if (ccs && cce && compDateRange === 'custom') {
      const start = new Date(ccs);
      const end = new Date(cce);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        compCustomDateRange = { start, end };
      }
    }

    const cRids = searchParams.get('cRegionIds');
    const compRegionIds =
      cRids !== null ? (cRids ? cRids.split(',') : []) : [...base.regionIds];

    const cPid = searchParams.get('cPhlebotomistId');
    const compPhlebotomistId =
      cPid !== null ? cPid || undefined : base.phlebotomistId;

    base.comparisonPaneFilters = {
      dateRange: compDateRange,
      customDateRange: compCustomDateRange,
      regionIds: compRegionIds,
      phlebotomistId: compPhlebotomistId,
    };
  }

  // Trends mode defaults
  if (base.displayMode === 'trends') {
    if (!base.trendsIntervalSize) base.trendsIntervalSize = 'week';
    // Only default to 'year' if no dateRange was set from URL
    if (!dr) base.dateRange = 'year';
  }

  return base;
}

/** Write dashboard filters to URL search params (returns new URLSearchParams) */
function filtersToUrl(filters: PhlebotomistDashboardFilters): URLSearchParams {
  const params = new URLSearchParams();

  // Only write non-default values to keep URLs clean
  if (filters.dateRange !== DEFAULT_FILTERS.dateRange) {
    params.set('dateRange', filters.dateRange);
  }
  if (filters.dateRange === 'custom' && filters.customDateRange) {
    params.set('customStart', filters.customDateRange.start.toISOString());
    params.set('customEnd', filters.customDateRange.end.toISOString());
  }
  if (filters.regionIds.length > 0) {
    params.set('regionIds', filters.regionIds.join(','));
  }
  if (filters.phlebotomistId) {
    params.set('phlebotomistId', filters.phlebotomistId);
  }
  if (filters.showRegionComparison) {
    params.set('showRegion', '1');
  }
  if (filters.showCompanyComparison) {
    params.set('showCompany', '1');
  }
  if (filters.displayMode !== 'normal') {
    params.set('displayMode', filters.displayMode);
  }
  if (filters.displayMode === 'trends' && filters.trendsIntervalSize) {
    params.set('trendsInterval', filters.trendsIntervalSize);
  }

  // Comparison pane filters (only for split/inline)
  const cp = filters.comparisonPaneFilters;
  if (
    cp &&
    (filters.displayMode === 'split' || filters.displayMode === 'inline')
  ) {
    params.set('cDateRange', cp.dateRange);
    if (cp.dateRange === 'custom' && cp.customDateRange) {
      params.set('cCustomStart', cp.customDateRange.start.toISOString());
      params.set('cCustomEnd', cp.customDateRange.end.toISOString());
    }
    if (cp.regionIds.length > 0) {
      params.set('cRegionIds', cp.regionIds.join(','));
    }
    if (cp.phlebotomistId) {
      params.set('cPhlebotomistId', cp.phlebotomistId);
    }
  }

  return params;
}

// Roles that can select phlebotomists (Admin/MD/DVM/Operations: any,
// TeamLead: office-scoped). Roles outside this list see only their own
// donation stats — a non-donating role left off this list gets an empty
// dashboard.
const CAN_SELECT_PHLEBOTOMIST_ROLES = [
  UserRole.Admin,
  UserRole.MedicalDirector,
  UserRole.Dvm,
  UserRole.TeamLead,
  UserRole.Operations,
];

const PhlebotomistDashboardContext =
  createContext<PhlebotomistDashboardContextValue | null>(null);

interface IProviderProps {
  children: ReactNode;
}

export const PhlebotomistDashboardProvider: FC<IProviderProps> = ({
  children,
}) => {
  const currentUser = useCurrentUser();

  // Load excluded user IDs from dashboard settings
  const { data: dashboardSettingsData } = useQuery(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: 'phlebotomist-dashboard' } },
  });
  const excludedUserIds: string[] = useMemo(() => {
    const json = dashboardSettingsData?.getAppSettings?.settingsJson as
      | { excludedUserIds?: string[] }
      | undefined;
    return json?.excludedUserIds || [];
  }, [dashboardSettingsData]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters with defaults + stored preferences + URL params
  const [filters, setFiltersState] = useState<PhlebotomistDashboardFilters>(
    () => filtersFromUrl(searchParams, loadPreferences())
  );

  // Track whether we're currently syncing URL → state to avoid loops
  const isSyncingRef = useRef(false);

  // Always fetch comparison data:
  // - In normal mode: auto-fetch previous period (for trend arrows)
  // - In compare modes: fetch the user-selected comparison period
  const effectiveComparisonDateRange =
    filters.displayMode !== 'normal'
      ? filters.comparisonPaneFilters?.dateRange
      : DATE_RANGE_PREVIOUS_MAP[filters.dateRange];
  const effectiveComparisonCustomDateRange =
    filters.displayMode !== 'normal'
      ? filters.comparisonPaneFilters?.customDateRange
      : undefined;

  const statsData = usePhlebotomistStatsData({
    dateRange: filters.dateRange,
    customDateRange: filters.customDateRange,
    comparisonDateRange: effectiveComparisonDateRange,
    comparisonCustomDateRange: effectiveComparisonCustomDateRange,
    excludedUserIds,
  });

  // ============ TREND DATA (only in trends mode) ============
  const isTrendsMode = filters.displayMode === 'trends';
  const intervalSize: TrendsIntervalSize = filters.trendsIntervalSize || 'week';

  // Calculate date bounds for trend query
  const trendDateBounds = useMemo(
    () => getDateRangeBounds(filters.dateRange, filters.customDateRange),
    [
      filters.dateRange,
      filters.customDateRange?.start?.getTime(),
      filters.customDateRange?.end?.getTime(),
    ]
  );

  // Fetch trend data from backend
  const trendStatsQuery = useGetPhlebotomistTrendStatsQuery({
    variables: {
      input: {
        dateFrom: trendDateBounds.start.toISOString(),
        dateTo: trendDateBounds.end.toISOString(),
        intervalSize: mapIntervalSizeToBackend(intervalSize),
        phlebotomistId: filters.phlebotomistId || undefined,
        regionIds: filters.regionIds.length > 0 ? filters.regionIds : undefined,
      },
    },
    skip: !isTrendsMode, // Only fetch when in trends mode
  });

  // Transform trend data for charts
  const trendData: TrendStatsDataPoint[] = useMemo(() => {
    if (!isTrendsMode || !trendStatsQuery.data?.getPhlebotomistTrendStats) {
      return [];
    }

    return trendStatsQuery.data.getPhlebotomistTrendStats.map((item) => ({
      period: item.period,
      periodStart: new Date(item.periodStart),
      periodEnd: new Date(item.periodEnd),
      attemptedDonations: item.attemptedDonations,
      successfulDonations: item.successfulDonations,
      singleDonors: item.singleDonors,
      doubleDonors: item.doubleDonors,
      successfulSingleDonors: item.successfulSingleDonors,
      successfulDoubleDonors: item.successfulDoubleDonors,
      expectedTotal: item.expectedTotal,
      expectedPRBC: item.expectedPRBC,
      expectedFFPSingle: item.expectedFFPSingle,
      expectedFFPDouble: item.expectedFFPDouble,
      actualTotal: item.actualTotal,
      actualPRBC: item.actualPRBC,
      actualFFPSingle: item.actualFFPSingle,
      actualFFPDouble: item.actualFFPDouble,
      overallSuccessRate: item.overallSuccessRate,
      prbcSuccessRate: item.prbcSuccessRate,
      sglDonorsSuccessRate: item.sglDonorsSuccessRate,
      dblDonorsSuccessRate: item.dblDonorsSuccessRate,
    }));
  }, [isTrendsMode, trendStatsQuery.data]);

  const trendDataLoading = isTrendsMode && trendStatsQuery.loading;

  // ── Admin Simulation ──────────────────────────────────────────
  const simulationSettings = useMemo(() => {
    const json = dashboardSettingsData?.getAppSettings?.settingsJson as
      | {
          simulateRole?: UserRole | null;
          simulateUserId?: string | null;
        }
      | undefined;
    if (!json?.simulateRole || json.simulateRole === UserRole.Admin)
      return null;
    return {
      simulateRole: json.simulateRole,
      simulateUserId: json.simulateUserId || null,
    };
  }, [dashboardSettingsData]);

  const actualUserRole = currentUser.data?.role?.value;
  const isActualAdmin = actualUserRole === UserRole.Admin;
  const isSimulating = isActualAdmin && !!simulationSettings;

  // Fetch simulated user data when simulation is active with a specific user
  const { data: simulatedUserData } = useGetUsersListQuery({
    variables: {
      input: {
        filter: { ids: [simulationSettings?.simulateUserId || ''] },
        take: 1,
      },
    },
    skip: !isSimulating || !simulationSettings?.simulateUserId,
  });
  const simulatedUser = useMemo(
    () => simulatedUserData?.getUsersList?.data?.[0] || null,
    [simulatedUserData]
  );

  // Effective role (simulated or actual)
  const effectiveRole = isSimulating
    ? simulationSettings!.simulateRole
    : actualUserRole;

  // Determine if current user can select phlebotomists
  const canSelectPhlebotomist = useMemo(() => {
    return effectiveRole
      ? CAN_SELECT_PHLEBOTOMIST_ROLES.includes(effectiveRole)
      : false;
  }, [effectiveRole]);

  // Determine if current user is Collection Team
  const isCollectionTeam = useMemo(() => {
    return effectiveRole === UserRole.CollectionTeam;
  }, [effectiveRole]);

  // Determine if current user is TeamLead
  const isTeamLead = useMemo(() => {
    return effectiveRole === UserRole.TeamLead;
  }, [effectiveRole]);

  // Determine if current user is Phlebotomist
  const isPhlebotomist = useMemo(() => {
    return effectiveRole === UserRole.Phlebotomist;
  }, [effectiveRole]);

  // UI visibility flags
  // Region filter: hidden for TeamLead (auto-locked to their region) and Phlebotomist
  const showRegionFilter = !isTeamLead && !isPhlebotomist;
  // Phlebotomist selector: hidden for Phlebotomist role (only sees own data)
  const showPhlebotomistFilter = canSelectPhlebotomist && !isPhlebotomist;

  // Current user ID (override with simulated user if active)
  const currentUserId =
    isSimulating && simulatedUser
      ? simulatedUser.id
      : currentUser.data?.id || null;

  // TeamLead's assigned office ID (for filtering phlebotomist list)
  const currentUserOfficeId =
    isSimulating && simulatedUser
      ? simulatedUser.assignedOffice?.id || null
      : currentUser.data?.assignedOffice?.id || null;
  // TeamLead's region ID (for auto-locking region filter)
  const currentUserRegionId =
    isSimulating && simulatedUser
      ? simulatedUser.productRegionPreference?.id || null
      : currentUser.data?.productRegionPreference?.id || null;

  // Simulation info for banner display
  const simulationInfo = useMemo(
    () =>
      isSimulating
        ? {
            role: simulationSettings!.simulateRole,
            userName: simulatedUser
              ? `${simulatedUser.firstName} ${simulatedUser.lastName}`
              : undefined,
            officeName: simulatedUser?.assignedOffice?.name || undefined,
          }
        : null,
    [isSimulating, simulationSettings, simulatedUser]
  );

  // Update filters helper
  const setFilters = useCallback(
    (updates: Partial<PhlebotomistDashboardFilters>) => {
      setFiltersState((prev) => {
        const newFilters = { ...prev, ...updates };

        // Persist toggle preferences
        if (
          updates.showRegionComparison !== undefined ||
          updates.showCompanyComparison !== undefined
        ) {
          savePreferences({
            showRegionComparison: newFilters.showRegionComparison,
            showCompanyComparison: newFilters.showCompanyComparison,
          });
        }

        return newFilters;
      });
    },
    []
  );

  // ============ SYNC FILTERS → URL ============
  // Keep URL search params in sync with filter state
  useEffect(() => {
    if (isSyncingRef.current) return;
    const newParams = filtersToUrl(filters);
    const currentStr = searchParams.toString();
    const newStr = newParams.toString();
    if (currentStr !== newStr) {
      isSyncingRef.current = true;
      setSearchParams(newParams, { replace: true });
      // Reset on next tick to allow future syncs
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  }, [filters, searchParams, setSearchParams]);

  // Auto-lock TeamLead's region filter on mount
  useEffect(() => {
    if (isTeamLead && currentUserRegionId) {
      // Only set if not already set to the correct region
      if (
        filters.regionIds.length !== 1 ||
        filters.regionIds[0] !== currentUserRegionId
      ) {
        setFiltersState((prev) => ({
          ...prev,
          regionIds: [currentUserRegionId],
        }));
      }
    }
  }, [isTeamLead, currentUserRegionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get phlebotomists filtered by region and office (for "All" aggregation)
  // TeamLead: only phlebotomists at the same assigned office
  const filteredPhlebotomists = useMemo(() => {
    let list = statsData.phlebotomists;

    // TeamLead: filter to same office only
    if (isTeamLead && currentUserOfficeId) {
      list = list.filter((ph) => ph.assignedOfficeId === currentUserOfficeId);
    }

    // Then apply region filter
    if (filters.regionIds.length > 0) {
      list = list.filter(
        (ph) => ph.regionId && filters.regionIds.includes(ph.regionId)
      );
    }

    return list;
  }, [
    statsData.phlebotomists,
    filters.regionIds,
    isTeamLead,
    currentUserOfficeId,
  ]);

  // Auto-select "All" when selected phlebotomist is not in filtered regions
  useEffect(() => {
    // Only apply when a specific phlebotomist is selected AND region filter is active
    if (!filters.phlebotomistId || filters.regionIds.length === 0) {
      return;
    }

    // Check if selected phlebotomist is in the filtered regions
    const selectedPhlebotomist = statsData.phlebotomists.find(
      (ph) => ph.id === filters.phlebotomistId
    );

    if (
      selectedPhlebotomist &&
      selectedPhlebotomist.regionId &&
      !filters.regionIds.includes(selectedPhlebotomist.regionId)
    ) {
      // Selected phlebotomist is not in filtered regions, auto-select "All"
      setFilters({ phlebotomistId: undefined });
    }
  }, [
    filters.phlebotomistId,
    filters.regionIds,
    statsData.phlebotomists,
    setFilters,
  ]);

  // Helper to aggregate stats for multiple phlebotomists
  const aggregatePhlebotomistStats = useMemo(() => {
    return (phlebotomistIds: string[]): PhlebotomistStats | null => {
      const statsToAggregate = phlebotomistIds
        .map((id) => statsData.allStats.get(id))
        .filter((s): s is PhlebotomistStats => s !== undefined);

      if (statsToAggregate.length === 0) return null;

      // Aggregate all the stats
      const aggregated: PhlebotomistStats = {
        phlebotomistId: '__ALL__',
        phlebotomistName: 'All Phlebotomists',
        regionId: '',
        regionName: '',
        attemptedDonations: statsToAggregate.reduce(
          (sum, s) => sum + s.attemptedDonations,
          0
        ),
        successfulDonations: statsToAggregate.reduce(
          (sum, s) => sum + s.successfulDonations,
          0
        ),
        pendingDonations: statsToAggregate.reduce(
          (sum, s) => sum + s.pendingDonations,
          0
        ),
        singleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.singleDonors,
          0
        ),
        doubleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.doubleDonors,
          0
        ),
        successfulSingleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.successfulSingleDonors,
          0
        ),
        successfulDoubleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.successfulDoubleDonors,
          0
        ),
        expectedTotal: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedTotal,
          0
        ),
        expectedPRBC: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedPRBC,
          0
        ),
        expectedPRBCSingle: statsToAggregate.reduce(
          (sum, s) => sum + (s.expectedPRBCSingle ?? 0),
          0
        ),
        expectedPRBCDouble: statsToAggregate.reduce(
          (sum, s) => sum + (s.expectedPRBCDouble ?? 0),
          0
        ),
        expectedFFPSingle: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedFFPSingle,
          0
        ),
        expectedFFPDouble: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedFFPDouble,
          0
        ),
        actualTotal: statsToAggregate.reduce(
          (sum, s) => sum + s.actualTotal,
          0
        ),
        actualPRBC: statsToAggregate.reduce((sum, s) => sum + s.actualPRBC, 0),
        actualPRBCSingle: statsToAggregate.reduce(
          (sum, s) => sum + (s.actualPRBCSingle ?? 0),
          0
        ),
        actualPRBCDouble: statsToAggregate.reduce(
          (sum, s) => sum + (s.actualPRBCDouble ?? 0),
          0
        ),
        actualFFPSingle: statsToAggregate.reduce(
          (sum, s) => sum + s.actualFFPSingle,
          0
        ),
        actualFFPDouble: statsToAggregate.reduce(
          (sum, s) => sum + s.actualFFPDouble,
          0
        ),
        dblDonorsSglYield: statsToAggregate.reduce(
          (sum, s) => sum + s.dblDonorsSglYield,
          0
        ),
        overallSuccessRate: 0,
        prbcSuccessRate: 0,
        sglDonorsSuccessRate: 0,
        dblDonorsSuccessRate: 0,
        completelyFailedDonations: statsToAggregate.reduce(
          (sum, s) => sum + (s.completelyFailedDonations ?? 0),
          0
        ),
        completelyFailedDiscards: (() => {
          const reasonMap = new Map<string, number>();
          statsToAggregate.forEach((s) => {
            (s.completelyFailedDiscards ?? []).forEach((dp) => {
              reasonMap.set(
                dp.reason,
                (reasonMap.get(dp.reason) || 0) + dp.count
              );
            });
          });
          return Array.from(reasonMap.entries()).map(([reason, count]) => ({
            reason,
            count,
          }));
        })(),
        partiallyFailedDonations: statsToAggregate.reduce(
          (sum, s) => sum + (s.partiallyFailedDonations ?? 0),
          0
        ),
        partiallyFailedDiscards: (() => {
          const reasonMap = new Map<string, number>();
          statsToAggregate.forEach((s) => {
            (s.partiallyFailedDiscards ?? []).forEach((dp) => {
              reasonMap.set(
                dp.reason,
                (reasonMap.get(dp.reason) || 0) + dp.count
              );
            });
          });
          return Array.from(reasonMap.entries()).map(([reason, count]) => ({
            reason,
            count,
          }));
        })(),
        recentDonations: statsToAggregate
          .flatMap((s) => s.recentDonations)
          .sort((a, b) => b.date.getTime() - a.date.getTime()),
      };

      // Calculate success rates (draw success: successful donations / attempted)
      aggregated.overallSuccessRate =
        aggregated.attemptedDonations > 0
          ? Math.round(
              (aggregated.successfulDonations / aggregated.attemptedDonations) *
                1000
            ) / 10
          : 0;
      aggregated.prbcSuccessRate =
        aggregated.expectedPRBC > 0
          ? Math.round(
              (aggregated.actualPRBC / aggregated.expectedPRBC) * 1000
            ) / 10
          : 0;

      const totalSingleDonors = statsToAggregate.reduce(
        (sum, s) => sum + s.singleDonors,
        0
      );
      const successfulSingles = statsToAggregate.reduce((sum, s) => {
        const successRate = s.sglDonorsSuccessRate / 100;
        return sum + Math.round(s.singleDonors * successRate);
      }, 0);
      aggregated.sglDonorsSuccessRate =
        totalSingleDonors > 0
          ? Math.round((successfulSingles / totalSingleDonors) * 1000) / 10
          : 0;

      const totalDoubleDonors = statsToAggregate.reduce(
        (sum, s) => sum + s.doubleDonors,
        0
      );
      const successfulDoubles = statsToAggregate.reduce((sum, s) => {
        const successRate = s.dblDonorsSuccessRate / 100;
        return sum + Math.round(s.doubleDonors * successRate);
      }, 0);
      aggregated.dblDonorsSuccessRate =
        totalDoubleDonors > 0
          ? Math.round((successfulDoubles / totalDoubleDonors) * 1000) / 10
          : 0;

      return aggregated;
    };
  }, [statsData.allStats]);

  // Get the current phlebotomist's stats
  const currentStats: PhlebotomistStats | null = useMemo(() => {
    if (statsData.loading) return null;

    // If user can select phlebotomist and has selected one, use that
    if (canSelectPhlebotomist && filters.phlebotomistId) {
      return statsData.allStats.get(filters.phlebotomistId) || null;
    }

    // If user cannot select (Collection Team), show their own stats
    if (!canSelectPhlebotomist && currentUserId) {
      return statsData.allStats.get(currentUserId) || null;
    }

    // If Admin/MD with no selection (All), aggregate stats for all (or region-filtered)
    if (canSelectPhlebotomist && !filters.phlebotomistId) {
      const idsToAggregate = filteredPhlebotomists.map((ph) => ph.id);
      return aggregatePhlebotomistStats(idsToAggregate);
    }

    return null;
  }, [
    statsData.loading,
    statsData.allStats,
    canSelectPhlebotomist,
    filters.phlebotomistId,
    currentUserId,
    filteredPhlebotomists,
    aggregatePhlebotomistStats,
  ]);

  // Office stats - phlebotomists at the same location(s) as current phlebotomist
  // Only show when: specific phlebotomist selected OR region(s) selected
  // Don't show when: "All phlebotomists" with no region filter (no specific office context)
  const officeStats: ComparisonStats | null = useMemo(() => {
    if (statsData.loading || !currentStats?.phlebotomistId) return null;

    // If Admin/MD with "All phlebotomists" (no specific phlebotomist selected)
    if (canSelectPhlebotomist && !filters.phlebotomistId) {
      // If no regions are selected, don't show office stats (no specific office context)
      if (filters.regionIds.length === 0) {
        return null;
      }
      // If regions are selected, we could show office stats for those regions
      // For now, we'll still use the aggregated phlebotomist's office stats
    }

    return statsData.officeStats(currentStats.phlebotomistId);
  }, [
    statsData.loading,
    statsData.officeStats,
    currentStats?.phlebotomistId,
    canSelectPhlebotomist,
    filters.phlebotomistId,
    filters.regionIds.length,
  ]);

  // Region stats - for Admin/MD with regions selected, use multi-region stats
  // For Collection Team or when no regions selected, use phlebotomist's region
  const regionStats: ComparisonStats | null = useMemo(() => {
    if (statsData.loading) return null;

    // Admin/MD with regions selected - show selected region(s) stats
    if (canSelectPhlebotomist && filters.regionIds.length > 0) {
      return statsData.multiRegionStats(filters.regionIds);
    }

    // Fall back to phlebotomist's region, then user's profile region
    const effectiveRegionId = currentStats?.regionId || currentUserRegionId;
    if (!effectiveRegionId) return null;
    return statsData.regionStats(effectiveRegionId);
  }, [
    statsData.loading,
    statsData.regionStats,
    statsData.multiRegionStats,
    currentStats?.regionId,
    currentUserRegionId,
    canSelectPhlebotomist,
    filters.regionIds,
  ]);

  // Company-wide stats
  const companyStats: ComparisonStats | null = useMemo(() => {
    if (statsData.loading) return null;
    return statsData.companyStats;
  }, [statsData.loading, statsData.companyStats]);

  // Full aggregated stats (with discards, donations) for side-by-side comparison cards
  const regionFullStats: PhlebotomistStats | null = useMemo(() => {
    if (statsData.loading) return null;
    if (canSelectPhlebotomist && filters.regionIds.length > 0) {
      return statsData.multiRegionFullStats(filters.regionIds);
    }
    // Use currentStats regionId first, fall back to user's profile region
    const effectiveRegionId = currentStats?.regionId || currentUserRegionId;
    if (!effectiveRegionId) return null;
    return statsData.regionFullStats(effectiveRegionId);
  }, [
    statsData.loading,
    statsData.regionFullStats,
    statsData.multiRegionFullStats,
    currentStats?.regionId,
    currentUserRegionId,
    canSelectPhlebotomist,
    filters.regionIds,
  ]);

  const companyFullStats: PhlebotomistStats | null = useMemo(() => {
    if (statsData.loading) return null;
    return statsData.companyFullStats;
  }, [statsData.loading, statsData.companyFullStats]);

  // ============ COMPARISON PANE STATS ============
  // Get filtered phlebotomists for comparison pane based on its region filter
  const comparisonFilteredPhlebotomists = useMemo(() => {
    const comparisonRegionIds = filters.comparisonPaneFilters?.regionIds || [];
    if (comparisonRegionIds.length === 0) {
      return statsData.phlebotomists;
    }
    return statsData.phlebotomists.filter(
      (ph) => ph.regionId && comparisonRegionIds.includes(ph.regionId)
    );
  }, [statsData.phlebotomists, filters.comparisonPaneFilters?.regionIds]);

  // Helper to aggregate comparison stats for multiple phlebotomists
  const aggregateComparisonPhlebotomistStats = useMemo(() => {
    return (phlebotomistIds: string[]): PhlebotomistStats | null => {
      const statsToAggregate = phlebotomistIds
        .map((id) => statsData.comparisonAllStats.get(id))
        .filter((s): s is PhlebotomistStats => s !== undefined);

      if (statsToAggregate.length === 0) return null;

      // Aggregate all the stats (same logic as aggregatePhlebotomistStats)
      const aggregated: PhlebotomistStats = {
        phlebotomistId: '__ALL__',
        phlebotomistName: 'All Phlebotomists',
        regionId: '',
        regionName: '',
        attemptedDonations: statsToAggregate.reduce(
          (sum, s) => sum + s.attemptedDonations,
          0
        ),
        successfulDonations: statsToAggregate.reduce(
          (sum, s) => sum + s.successfulDonations,
          0
        ),
        pendingDonations: statsToAggregate.reduce(
          (sum, s) => sum + s.pendingDonations,
          0
        ),
        singleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.singleDonors,
          0
        ),
        doubleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.doubleDonors,
          0
        ),
        successfulSingleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.successfulSingleDonors,
          0
        ),
        successfulDoubleDonors: statsToAggregate.reduce(
          (sum, s) => sum + s.successfulDoubleDonors,
          0
        ),
        expectedTotal: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedTotal,
          0
        ),
        expectedPRBC: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedPRBC,
          0
        ),
        expectedPRBCSingle: statsToAggregate.reduce(
          (sum, s) => sum + (s.expectedPRBCSingle ?? 0),
          0
        ),
        expectedPRBCDouble: statsToAggregate.reduce(
          (sum, s) => sum + (s.expectedPRBCDouble ?? 0),
          0
        ),
        expectedFFPSingle: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedFFPSingle,
          0
        ),
        expectedFFPDouble: statsToAggregate.reduce(
          (sum, s) => sum + s.expectedFFPDouble,
          0
        ),
        actualTotal: statsToAggregate.reduce(
          (sum, s) => sum + s.actualTotal,
          0
        ),
        actualPRBC: statsToAggregate.reduce((sum, s) => sum + s.actualPRBC, 0),
        actualPRBCSingle: statsToAggregate.reduce(
          (sum, s) => sum + (s.actualPRBCSingle ?? 0),
          0
        ),
        actualPRBCDouble: statsToAggregate.reduce(
          (sum, s) => sum + (s.actualPRBCDouble ?? 0),
          0
        ),
        actualFFPSingle: statsToAggregate.reduce(
          (sum, s) => sum + s.actualFFPSingle,
          0
        ),
        actualFFPDouble: statsToAggregate.reduce(
          (sum, s) => sum + s.actualFFPDouble,
          0
        ),
        dblDonorsSglYield: statsToAggregate.reduce(
          (sum, s) => sum + s.dblDonorsSglYield,
          0
        ),
        overallSuccessRate: 0,
        prbcSuccessRate: 0,
        sglDonorsSuccessRate: 0,
        dblDonorsSuccessRate: 0,
        completelyFailedDonations: statsToAggregate.reduce(
          (sum, s) => sum + (s.completelyFailedDonations ?? 0),
          0
        ),
        completelyFailedDiscards: (() => {
          const reasonMap = new Map<string, number>();
          statsToAggregate.forEach((s) => {
            (s.completelyFailedDiscards ?? []).forEach((dp) => {
              reasonMap.set(
                dp.reason,
                (reasonMap.get(dp.reason) || 0) + dp.count
              );
            });
          });
          return Array.from(reasonMap.entries()).map(([reason, count]) => ({
            reason,
            count,
          }));
        })(),
        partiallyFailedDonations: statsToAggregate.reduce(
          (sum, s) => sum + (s.partiallyFailedDonations ?? 0),
          0
        ),
        partiallyFailedDiscards: (() => {
          const reasonMap = new Map<string, number>();
          statsToAggregate.forEach((s) => {
            (s.partiallyFailedDiscards ?? []).forEach((dp) => {
              reasonMap.set(
                dp.reason,
                (reasonMap.get(dp.reason) || 0) + dp.count
              );
            });
          });
          return Array.from(reasonMap.entries()).map(([reason, count]) => ({
            reason,
            count,
          }));
        })(),
        recentDonations: statsToAggregate
          .flatMap((s) => s.recentDonations)
          .sort((a, b) => b.date.getTime() - a.date.getTime()),
      };

      // Calculate success rates (draw success: successful donations / attempted)
      aggregated.overallSuccessRate =
        aggregated.attemptedDonations > 0
          ? Math.round(
              (aggregated.successfulDonations / aggregated.attemptedDonations) *
                1000
            ) / 10
          : 0;
      aggregated.prbcSuccessRate =
        aggregated.expectedPRBC > 0
          ? Math.round(
              (aggregated.actualPRBC / aggregated.expectedPRBC) * 1000
            ) / 10
          : 0;

      const totalSingleDonors = statsToAggregate.reduce(
        (sum, s) => sum + s.singleDonors,
        0
      );
      const successfulSingles = statsToAggregate.reduce((sum, s) => {
        const successRate = s.sglDonorsSuccessRate / 100;
        return sum + Math.round(s.singleDonors * successRate);
      }, 0);
      aggregated.sglDonorsSuccessRate =
        totalSingleDonors > 0
          ? Math.round((successfulSingles / totalSingleDonors) * 1000) / 10
          : 0;

      const totalDoubleDonors = statsToAggregate.reduce(
        (sum, s) => sum + s.doubleDonors,
        0
      );
      const successfulDoubles = statsToAggregate.reduce((sum, s) => {
        const successRate = s.dblDonorsSuccessRate / 100;
        return sum + Math.round(s.doubleDonors * successRate);
      }, 0);
      aggregated.dblDonorsSuccessRate =
        totalDoubleDonors > 0
          ? Math.round((successfulDoubles / totalDoubleDonors) * 1000) / 10
          : 0;

      return aggregated;
    };
  }, [statsData.comparisonAllStats]);

  // Get comparison pane stats
  const comparisonStats: PhlebotomistStats | null = useMemo(() => {
    if (statsData.loading || filters.displayMode === 'normal') return null;
    if (statsData.comparisonAllStats.size === 0) return null;

    const comparisonFilters = filters.comparisonPaneFilters;

    // If user can select phlebotomist and has selected one, use that
    if (canSelectPhlebotomist && comparisonFilters?.phlebotomistId) {
      return (
        statsData.comparisonAllStats.get(comparisonFilters.phlebotomistId) ||
        null
      );
    }

    // If user cannot select (Collection Team), show their own stats
    if (!canSelectPhlebotomist && currentUserId) {
      return statsData.comparisonAllStats.get(currentUserId) || null;
    }

    // If Admin/MD with no selection (All), aggregate stats for all (or region-filtered)
    if (canSelectPhlebotomist && !comparisonFilters?.phlebotomistId) {
      const idsToAggregate = comparisonFilteredPhlebotomists.map((ph) => ph.id);
      return aggregateComparisonPhlebotomistStats(idsToAggregate);
    }

    return null;
  }, [
    statsData.loading,
    statsData.comparisonAllStats,
    filters.displayMode,
    filters.comparisonPaneFilters,
    canSelectPhlebotomist,
    currentUserId,
    comparisonFilteredPhlebotomists,
    aggregateComparisonPhlebotomistStats,
  ]);

  // Previous period stats — same aggregation as currentStats but using comparison data
  // Available in ALL modes (normal mode uses it for trend arrows)
  const previousPeriodStats: PhlebotomistStats | null = useMemo(() => {
    if (statsData.loading || statsData.comparisonAllStats.size === 0)
      return null;

    // Mirror the same selection logic as currentStats but against comparison data
    if (canSelectPhlebotomist && filters.phlebotomistId) {
      return statsData.comparisonAllStats.get(filters.phlebotomistId) || null;
    }
    if (!canSelectPhlebotomist && currentUserId) {
      return statsData.comparisonAllStats.get(currentUserId) || null;
    }
    if (canSelectPhlebotomist && !filters.phlebotomistId) {
      const idsToAggregate = filteredPhlebotomists.map((ph) => ph.id);
      return aggregateComparisonPhlebotomistStats(idsToAggregate);
    }
    return null;
  }, [
    statsData.loading,
    statsData.comparisonAllStats,
    canSelectPhlebotomist,
    filters.phlebotomistId,
    currentUserId,
    filteredPhlebotomists,
    aggregateComparisonPhlebotomistStats,
  ]);

  // Comparison office stats - same logic as primary office stats
  const comparisonOfficeStats: ComparisonStats | null = useMemo(() => {
    if (
      statsData.loading ||
      filters.displayMode === 'normal' ||
      !comparisonStats?.phlebotomistId
    )
      return null;

    // If Admin/MD with "All phlebotomists" (no specific phlebotomist selected in comparison pane)
    if (
      canSelectPhlebotomist &&
      !filters.comparisonPaneFilters?.phlebotomistId
    ) {
      // If no regions are selected in comparison pane, don't show office stats
      const comparisonRegionIds =
        filters.comparisonPaneFilters?.regionIds || [];
      if (comparisonRegionIds.length === 0) {
        return null;
      }
    }

    return statsData.comparisonOfficeStats(comparisonStats.phlebotomistId);
  }, [
    statsData.loading,
    statsData.comparisonOfficeStats,
    filters.displayMode,
    comparisonStats?.phlebotomistId,
    canSelectPhlebotomist,
    filters.comparisonPaneFilters?.phlebotomistId,
    filters.comparisonPaneFilters?.regionIds,
  ]);

  // Comparison region stats - for Admin/MD with regions selected, use multi-region stats
  const comparisonRegionStats: ComparisonStats | null = useMemo(() => {
    if (statsData.loading || filters.displayMode === 'normal') return null;

    const comparisonRegionIds = filters.comparisonPaneFilters?.regionIds || [];

    // Admin/MD with regions selected - show selected region(s) stats
    if (canSelectPhlebotomist && comparisonRegionIds.length > 0) {
      return statsData.comparisonMultiRegionStats(comparisonRegionIds);
    }

    // Fall back to phlebotomist's region
    if (!comparisonStats?.regionId) return null;
    return statsData.comparisonRegionStats(comparisonStats.regionId);
  }, [
    statsData.loading,
    statsData.comparisonRegionStats,
    statsData.comparisonMultiRegionStats,
    filters.displayMode,
    filters.comparisonPaneFilters?.regionIds,
    comparisonStats?.regionId,
    canSelectPhlebotomist,
  ]);

  // Comparison company stats
  const comparisonCompanyStats: ComparisonStats | null = useMemo(() => {
    if (statsData.loading || filters.displayMode === 'normal') return null;
    return statsData.comparisonCompanyStats;
  }, [
    statsData.loading,
    statsData.comparisonCompanyStats,
    filters.displayMode,
  ]);

  // ============ GOAL DATA ============
  // Goal data is only shown when a specific phlebotomist is selected
  // (either the current user for CT, or a specific selection for Admin/MD)
  const goalData: DonationGoalData | null = useMemo(() => {
    // Only show goal when a specific phlebotomist is selected
    const phlebotomistId =
      (!canSelectPhlebotomist && currentUserId) || // CT viewing their own
      (canSelectPhlebotomist && filters.phlebotomistId); // Admin/MD with specific selection

    if (!phlebotomistId || typeof phlebotomistId !== 'string') return null;

    // Get the phlebotomist's stats and location info
    const phlebStats = statsData.allStats.get(phlebotomistId);
    const phlebotomist = statsData.phlebotomists.find(
      (p) => p.id === phlebotomistId
    );
    if (!phlebStats || !phlebotomist) return null;

    // Get office stats (all CT at the same assignedOffice)
    const officeCompStats = statsData.officeStats(phlebotomistId);

    // Count phlebotomists at this office (using assignedOfficeId)
    const phlebotomistsAtLocation = phlebotomist.assignedOfficeId
      ? statsData.phlebotomists.filter(
          (p) => p.assignedOfficeId === phlebotomist.assignedOfficeId
        )
      : [];
    const numPhlebotomistsAtLocation = phlebotomistsAtLocation.length;

    // Calculate goalQty using the formula:
    // goalQty = totalOfficeSuccessful * ((1 / numPhlebotomistsAtLocation) - 0.03)
    // If there are 3 phlebotomists, each one's goal is ~30% of office total (33% - 3%)
    // Note: successful donations = attempted - failures (ComparisonStats doesn't expose successfulDonations directly)
    const totalOfficeSuccessful =
      officeCompStats.overall.attempted - officeCompStats.overall.failures;

    // Calculate office share percentage (goal as % of office total)
    const officeSharePercent =
      numPhlebotomistsAtLocation > 0
        ? Math.round((1 / numPhlebotomistsAtLocation - 0.03) * 100)
        : 0;

    // Calculate donation share percentage (this CT's successful / office total)
    // Round to 1 decimal place for more precision
    const donationSharePercent =
      totalOfficeSuccessful > 0
        ? Math.round(
            (phlebStats.successfulDonations / totalOfficeSuccessful) * 1000
          ) / 10
        : 0;

    let goalQty = 0;
    if (numPhlebotomistsAtLocation > 0 && totalOfficeSuccessful > 0) {
      const proportionalFactor = 1 / numPhlebotomistsAtLocation - 0.03;
      goalQty = Math.round(totalOfficeSuccessful * proportionalFactor);
      // Ensure minimum goal of 1
      if (goalQty <= 0) {
        goalQty = Math.max(
          Math.round(totalOfficeSuccessful / numPhlebotomistsAtLocation),
          1
        );
      }
    } else {
      goalQty = 10; // Default minimum goal
    }

    // Use actual filter date range
    const dateRange = getDateRangeBounds(
      filters.dateRange,
      filters.customDateRange
    );

    return {
      goalQty,
      periodLabel: filters.dateRange,
      dateRangeStart: dateRange.start,
      dateRangeEnd: dateRange.end,
      teamCount: numPhlebotomistsAtLocation,
      officeSharePercent,
      donationSharePercent,
      totalOfficeSuccessful,
    };
  }, [
    canSelectPhlebotomist,
    currentUserId,
    filters.phlebotomistId,
    filters.dateRange,
    filters.customDateRange,
    statsData.allStats,
    statsData.phlebotomists,
    statsData.officeStats,
  ]);

  // Comparison goal data (uses comparison date range stats)
  const comparisonGoalData: DonationGoalData | null = useMemo(() => {
    if (filters.displayMode === 'normal') return null;

    const comparisonFilters = filters.comparisonPaneFilters;
    // For CT users, always use their own ID
    // For Admin/MD, use comparison pane selection, or fall back to primary pane selection
    const phlebotomistId =
      (!canSelectPhlebotomist && currentUserId) ||
      (canSelectPhlebotomist &&
        (comparisonFilters?.phlebotomistId || filters.phlebotomistId));

    if (!phlebotomistId || typeof phlebotomistId !== 'string') return null;

    // Get the phlebotomist's comparison stats
    const phlebStats = statsData.comparisonAllStats.get(phlebotomistId);
    const phlebotomist = statsData.phlebotomists.find(
      (p) => p.id === phlebotomistId
    );
    if (!phlebStats || !phlebotomist) return null;

    // Get office stats for comparison period
    const officeCompStats = statsData.comparisonOfficeStats(phlebotomistId);

    // Count phlebotomists at this office (using assignedOfficeId)
    const phlebotomistsAtLocation = phlebotomist.assignedOfficeId
      ? statsData.phlebotomists.filter(
          (p) => p.assignedOfficeId === phlebotomist.assignedOfficeId
        )
      : [];
    const numPhlebotomistsAtLocation = phlebotomistsAtLocation.length;

    // Calculate goalQty using the same formula as primary
    // Note: successful donations = attempted - failures
    const totalOfficeSuccessful =
      officeCompStats.overall.attempted - officeCompStats.overall.failures;

    let goalQty = 0;
    if (numPhlebotomistsAtLocation > 0 && totalOfficeSuccessful > 0) {
      const proportionalFactor = 1 / numPhlebotomistsAtLocation - 0.03;
      goalQty = Math.round(totalOfficeSuccessful * proportionalFactor);
      if (goalQty <= 0) {
        goalQty = Math.max(
          Math.round(totalOfficeSuccessful / numPhlebotomistsAtLocation),
          1
        );
      }
    } else {
      goalQty = 10;
    }

    // Use actual comparison filter date range
    const comparisonDateRange = getDateRangeBounds(
      comparisonFilters?.dateRange || filters.dateRange,
      comparisonFilters?.customDateRange || filters.customDateRange
    );

    // Calculate office share percentage (goal as % of office total)
    const officeSharePercent =
      numPhlebotomistsAtLocation > 0
        ? Math.round((1 / numPhlebotomistsAtLocation - 0.03) * 100)
        : 0;

    // Calculate donation share percentage (this CT's successful / office total)
    // Round to 1 decimal place for more precision
    const donationSharePercent =
      totalOfficeSuccessful > 0
        ? Math.round(
            (phlebStats.successfulDonations / totalOfficeSuccessful) * 1000
          ) / 10
        : 0;

    return {
      goalQty,
      periodLabel: comparisonFilters?.dateRange || filters.dateRange,
      dateRangeStart: comparisonDateRange.start,
      dateRangeEnd: comparisonDateRange.end,
      teamCount: numPhlebotomistsAtLocation,
      officeSharePercent,
      donationSharePercent,
      totalOfficeSuccessful,
    };
  }, [
    filters.displayMode,
    filters.dateRange,
    filters.customDateRange,
    filters.phlebotomistId,
    filters.comparisonPaneFilters,
    canSelectPhlebotomist,
    currentUserId,
    statsData.comparisonAllStats,
    statsData.phlebotomists,
    statsData.comparisonOfficeStats,
  ]);

  // ============ DISPLAY MODE HELPERS ============
  // Set display mode and initialize comparison pane filters
  const setDisplayMode = useCallback(
    (mode: DashboardDisplayMode) => {
      if (mode === 'normal') {
        setFilters({ displayMode: mode, comparisonPaneFilters: undefined });
      } else if (mode === 'trends') {
        // Trends mode: default to year date range and week interval
        setFilters({
          displayMode: mode,
          dateRange: 'year',
          trendsIntervalSize: 'week',
          comparisonPaneFilters: undefined,
        });
      } else {
        // Initialize comparison pane with same filters but "previous" date range
        const previousDateRange = DATE_RANGE_PREVIOUS_MAP[filters.dateRange];
        setFilters({
          displayMode: mode,
          comparisonPaneFilters: {
            dateRange: previousDateRange,
            customDateRange: undefined, // Don't copy custom range
            regionIds: [...filters.regionIds],
            phlebotomistId: filters.phlebotomistId,
          },
        });
      }
    },
    [filters.dateRange, filters.regionIds, filters.phlebotomistId, setFilters]
  );

  // Reset to normal mode
  const resetDisplayMode = useCallback(() => {
    setFilters({ displayMode: 'normal', comparisonPaneFilters: undefined });
  }, [setFilters]);

  // Update comparison pane filters
  const setComparisonFilters = useCallback((updates: Partial<PaneFilters>) => {
    setFiltersState((prev) => ({
      ...prev,
      comparisonPaneFilters: prev.comparisonPaneFilters
        ? { ...prev.comparisonPaneFilters, ...updates }
        : {
            dateRange: updates.dateRange || prev.dateRange,
            regionIds: updates.regionIds || prev.regionIds,
            phlebotomistId: updates.phlebotomistId,
            customDateRange: updates.customDateRange,
          },
    }));
  }, []);

  const contextValue: PhlebotomistDashboardContextValue = {
    filters,
    setFilters,
    setComparisonFilters,
    currentStats,
    officeStats,
    regionStats,
    companyStats,
    regionFullStats,
    companyFullStats,
    goalData,
    previousPeriodStats,
    comparisonStats,
    comparisonOfficeStats,
    comparisonRegionStats,
    comparisonCompanyStats,
    comparisonGoalData,
    trendData,
    trendDataLoading,
    setDisplayMode,
    resetDisplayMode,
    // For TeamLead, pass office-filtered list so dropdown only shows same-office phlebotomists
    phlebotomists: isTeamLead ? filteredPhlebotomists : statsData.phlebotomists,
    regions: statsData.regions,
    loading: statsData.loading || currentUser.isLoading,
    canSelectPhlebotomist,
    currentUserId,
    isCollectionTeam,
    isTeamLead,
    isPhlebotomist,
    showRegionFilter,
    showPhlebotomistFilter,
    hasRegion: !!currentUserRegionId,
    isSimulating,
    simulationInfo,
  };

  return (
    <PhlebotomistDashboardContext.Provider value={contextValue}>
      {children}
    </PhlebotomistDashboardContext.Provider>
  );
};

export const usePhlebotomistDashboard =
  (): PhlebotomistDashboardContextValue => {
    const context = useContext(PhlebotomistDashboardContext);
    if (!context) {
      throw new Error(
        'usePhlebotomistDashboard must be used within a PhlebotomistDashboardProvider'
      );
    }
    return context;
  };
